import React, { useCallback, useMemo, useState } from "react";
import type { Task, TaskRecurringRule } from "@meupedaco/shared";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { PageState } from "../../components/PageState";
import { CrudModal } from "../../components/CrudModal";
import { useAuth } from "../../auth/AuthContext";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import {
  TASK_CADENCE_LABEL,
  TASK_PRIORITY_LABEL,
  TASK_STATUS_LABEL,
} from "../../lib/i18n/tasks";

type Option = { id: number; label: string };

const statusOptions = Object.entries(TASK_STATUS_LABEL).map(([value, label]) => ({
  value,
  label,
}));

const priorityOptions = Object.entries(TASK_PRIORITY_LABEL).map(([value, label]) => ({
  value,
  label,
}));

const cadenceOptions = Object.entries(TASK_CADENCE_LABEL).map(([value, label]) => ({
  value,
  label,
}));

const weekdays = [
  { value: "0", label: "Domingo" },
  { value: "1", label: "Segunda" },
  { value: "2", label: "Terca" },
  { value: "3", label: "Quarta" },
  { value: "4", label: "Quinta" },
  { value: "5", label: "Sexta" },
  { value: "6", label: "Sabado" },
];

export const TaskRecurringPage: React.FC = () => {
  const { client, user } = useAuth();
  const [activeFilter, setActiveFilter] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"rules" | "instances">("rules");
  const [selectedRule, setSelectedRule] = useState<TaskRecurringRule | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [status, setStatus] = useState("OPEN");
  const [cadence, setCadence] = useState("MONTHLY");
  const [dayOfMonth, setDayOfMonth] = useState("");
  const [dayOfWeek, setDayOfWeek] = useState("");
  const [monthOfYear, setMonthOfYear] = useState("");
  const [timeOfDay, setTimeOfDay] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [assignees, setAssignees] = useState<Option[]>([]);

  const canManage = Boolean(user?.permissions?.includes("TASK_MANAGE_RECURRING"));

  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<TaskRecurringRule>({
    endpoint: "/tasks/recurring",
    filters: {
      isActive: activeFilter === "" ? undefined : activeFilter === "true",
    },
    orderBy: "nextRunAt",
    orderDir: "asc",
  });

  const {
    rows: instanceRows,
    total: instanceTotal,
    loading: instanceLoading,
    error: instanceError,
    page: instancePage,
    take: instanceTake,
    setPage: setInstancePage,
    reload: reloadInstances,
  } = usePagedQuery<Task>({
    endpoint: selectedRule ? `/tasks/recurring/${selectedRule.id}/instances` : "/tasks/recurring/0/instances",
    enabled: Boolean(selectedRule),
    orderBy: "dueDate",
    orderDir: "desc",
  });

  const fetchAssignees = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/tasks/assignees?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string; email: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.name} (${item.email})`,
      }));
    },
    [client]
  );

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setStatus("OPEN");
    setCadence("MONTHLY");
    setDayOfMonth("");
    setDayOfWeek("");
    setMonthOfYear("");
    setTimeOfDay("");
    setStartDate("");
    setEndDate("");
    setIsActive(true);
    setAssignees([]);
  };

  const openModal = (mode: "create" | "edit" | "view", rule?: TaskRecurringRule) => {
    setModalMode(mode);
    if (mode === "create") {
      resetForm();
    } else if (rule) {
      setEditingId(rule.id);
      setTitle(rule.titleTemplate);
      setDescription(rule.descriptionTemplate ?? "");
      setPriority(rule.priority ?? "MEDIUM");
      setStatus(rule.status ?? "OPEN");
      setCadence(rule.cadence ?? "MONTHLY");
      setDayOfMonth(rule.dayOfMonth ? String(rule.dayOfMonth) : "");
      setDayOfWeek(rule.dayOfWeek !== null && rule.dayOfWeek !== undefined ? String(rule.dayOfWeek) : "");
      setMonthOfYear(rule.monthOfYear ? String(rule.monthOfYear) : "");
      setTimeOfDay(rule.timeOfDay ?? "");
      setStartDate(rule.startDate ? rule.startDate.slice(0, 10) : "");
      setEndDate(rule.endDate ? rule.endDate.slice(0, 10) : "");
      setIsActive(rule.isActive);
      setAssignees(
        (rule.assignees ?? []).map((assignee) => ({
          id: assignee.userId,
          label: assignee.user
            ? `${assignee.user.name} (${assignee.user.email})`
            : String(assignee.userId),
        }))
      );
    }
    setModalOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const payload = {
      titleTemplate: title,
      descriptionTemplate: description || null,
      priority,
      status,
      cadence,
      dayOfMonth: dayOfMonth ? Number(dayOfMonth) : null,
      dayOfWeek: dayOfWeek ? Number(dayOfWeek) : null,
      monthOfYear: monthOfYear ? Number(monthOfYear) : null,
      timeOfDay: timeOfDay || null,
      startDate,
      endDate: endDate || null,
      isActive,
      assigneeIds: assignees.map((item) => item.id),
    };
    const response = editingId
      ? await client.put(`/tasks/recurring/${editingId}`, payload)
      : await client.post("/tasks/recurring", payload);
    if (!response.ok) {
      setMessage("Falha ao salvar recorrencia.");
      return;
    }
    setModalOpen(false);
    resetForm();
    await reload();
  };

  const handleAction = async (rule: TaskRecurringRule, action: "pause" | "resume" | "run-now") => {
    setMessage(null);
    const response = await client.post(`/tasks/recurring/${rule.id}/${action}`, {});
    if (!response.ok) {
      setMessage("Falha ao executar acao.");
      return;
    }
    await reload();
    if (selectedRule?.id === rule.id) {
      await reloadInstances();
    }
  };

  const paginationText = useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  const instancePaginationText = useMemo(() => {
    if (instanceTotal === 0) {
      return "0 resultados";
    }
    const start = (instancePage - 1) * instanceTake + 1;
    const end = Math.min(instancePage * instanceTake, instanceTotal);
    return `${start} - ${end} de ${instanceTotal}`;
  }, [instancePage, instanceTake, instanceTotal]);

  const readOnly = modalMode === "view" || !canManage;
  const alertMessage = message ?? error ?? instanceError ?? null;

  return (
    <>
      <div className="container py-4">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div>
            <h1 className="mb-1">Tarefas recorrentes</h1>
            <div className="btn-group">
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "rules" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("rules")}
              >
                Regras
              </button>
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "instances" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setActiveTab("instances")}
              >
                Instancias
              </button>
            </div>
          </div>
          {canManage && activeTab === "rules" && (
            <button className="btn btn-primary" type="button" onClick={() => openModal("create")}>
              Nova recorrencia
            </button>
          )}
        </div>

        {activeTab === "rules" && (
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-2">
                  <label className="form-label">Ativo</label>
                  <select
                    className="form-select"
                    value={activeFilter}
                    onChange={(event) => setActiveFilter(event.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="true">Sim</option>
                    <option value="false">Nao</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Busca</label>
                  <input
                    className="form-control"
                    placeholder="Buscar por titulo"
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {alertMessage && <div className="alert alert-danger">{alertMessage}</div>}

        {activeTab === "rules" ? (
          <div className="card shadow-sm">
            <div className="card-body">
              <PageState loading={loading} error={error}>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Titulo</th>
                        <th>Cadencia</th>
                        <th>Proxima</th>
                        <th>Ativa</th>
                        <th>Responsaveis</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((rule) => (
                        <tr key={rule.id}>
                          <td>{rule.titleTemplate}</td>
                          <td>{TASK_CADENCE_LABEL[rule.cadence] ?? rule.cadence}</td>
                          <td>{rule.nextRunAt ? rule.nextRunAt.slice(0, 16).replace("T", " ") : "-"}</td>
                          <td>{rule.isActive ? "Sim" : "Nao"}</td>
                          <td>
                            {(rule.assignees ?? [])
                              .map((assignee) =>
                                assignee.user ? assignee.user.name : String(assignee.userId)
                              )
                              .join(", ") || "-"}
                          </td>
                          <td className="text-end">
                            <div className="btn-group">
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => {
                                  setSelectedRule(rule);
                                  setActiveTab("instances");
                                }}
                              >
                                Instancias
                              </button>
                              {canManage ? (
                                <>
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => openModal("edit", rule)}
                                  >
                                    Editar
                                  </button>
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => handleAction(rule, "run-now")}
                                  >
                                    Rodar agora
                                  </button>
                                  {rule.isActive ? (
                                    <button
                                      className="btn btn-outline-warning btn-sm"
                                      onClick={() => handleAction(rule, "pause")}
                                    >
                                      Pausar
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-outline-success btn-sm"
                                      onClick={() => handleAction(rule, "resume")}
                                    >
                                      Retomar
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => openModal("view", rule)}
                                >
                                  Ver
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            Nenhuma recorrencia encontrada.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PageState>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{paginationText}</small>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page * take >= total}
                    onClick={() => setPage(page + 1)}
                  >
                    Proximo
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                <div>
                  <strong>Instancias</strong>
                  {selectedRule && (
                    <div className="text-muted small">{selectedRule.titleTemplate}</div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setActiveTab("rules")}
                >
                  Voltar
                </button>
              </div>
              {!selectedRule && (
                <div className="text-muted">Selecione uma recorrencia para ver as instancias.</div>
              )}
              {selectedRule && (
                <>
                  <PageState loading={instanceLoading} error={instanceError}>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Titulo</th>
                            <th>Vencimento</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {instanceRows.map((task) => (
                            <tr key={task.id}>
                              <td>{task.title}</td>
                              <td>{task.dueDate ? task.dueDate.slice(0, 16).replace("T", " ") : "-"}</td>
                              <td>{TASK_STATUS_LABEL[task.status] ?? task.status}</td>
                            </tr>
                          ))}
                          {instanceRows.length === 0 && (
                            <tr>
                              <td colSpan={3} className="text-center text-muted">
                                Nenhuma instancia encontrada.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </PageState>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{instancePaginationText}</small>
                    <div className="btn-group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={instancePage <= 1}
                        onClick={() => setInstancePage(Math.max(1, instancePage - 1))}
                      >
                        Anterior
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={instancePage * instanceTake >= instanceTotal}
                        onClick={() => setInstancePage(instancePage + 1)}
                      >
                        Proximo
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <CrudModal
        title={
          modalMode === "create"
            ? "Nova recorrencia"
            : modalMode === "edit"
              ? "Editar recorrencia"
              : "Detalhes da recorrencia"
        }
        open={modalOpen}
        mode={modalMode}
        canWrite={canManage}
        onClose={() => setModalOpen(false)}
        formId="task-recurring-form"
      >
        <form onSubmit={handleSubmit} id="task-recurring-form">
          <div className="mb-3">
            <label className="form-label">Titulo base</label>
            <input
              className="form-control"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              disabled={readOnly}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Descricao</label>
            <textarea
              className="form-control"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              disabled={readOnly}
            />
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                disabled={readOnly}
              >
                {statusOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Prioridade</label>
              <select
                className="form-select"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
                disabled={readOnly}
              >
                {priorityOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Cadencia</label>
              <select
                className="form-select"
                value={cadence}
                onChange={(event) => {
                  const value = event.target.value;
                  setCadence(value);
                  if (value !== "MONTHLY" && value !== "YEARLY") {
                    setDayOfMonth("");
                  }
                  if (value !== "WEEKLY") {
                    setDayOfWeek("");
                  }
                  if (value !== "YEARLY") {
                    setMonthOfYear("");
                  }
                }}
                disabled={readOnly}
              >
                {cadenceOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            {(cadence === "MONTHLY" || cadence === "YEARLY") && (
              <div className="col-md-4">
                <label className="form-label">Dia do mes</label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  max={31}
                  value={dayOfMonth}
                  onChange={(event) => setDayOfMonth(event.target.value)}
                  disabled={readOnly}
                />
              </div>
            )}
            {cadence === "WEEKLY" && (
              <div className="col-md-4">
                <label className="form-label">Dia da semana</label>
                <select
                  className="form-select"
                  value={dayOfWeek}
                  onChange={(event) => setDayOfWeek(event.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Selecionar</option>
                  {weekdays.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {cadence === "YEARLY" && (
              <div className="col-md-4">
                <label className="form-label">Mes do ano</label>
                <input
                  className="form-control"
                  type="number"
                  min={1}
                  max={12}
                  value={monthOfYear}
                  onChange={(event) => setMonthOfYear(event.target.value)}
                  disabled={readOnly}
                />
              </div>
            )}
            <div className="col-md-4">
              <label className="form-label">Horario</label>
              <input
                className="form-control"
                type="time"
                value={timeOfDay}
                onChange={(event) => setTimeOfDay(event.target.value)}
                disabled={readOnly}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Inicio</label>
              <input
                className="form-control"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                required
                disabled={readOnly}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Fim</label>
              <input
                className="form-control"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                disabled={readOnly}
              />
            </div>
            <div className="col-md-4">
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                  disabled={readOnly}
                  id="rule-active"
                />
                <label className="form-check-label" htmlFor="rule-active">
                  Ativo
                </label>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <AutocompleteInput
              label="Responsaveis"
              placeholder="Buscar usuario"
              value={null}
              selectedLabel=""
              fetchOptions={fetchAssignees}
              disabled={readOnly}
              onSelect={(option) => {
                if (!option) {
                  return;
                }
                setAssignees((prev) =>
                  prev.some((item) => item.id === option.id) ? prev : [...prev, option]
                );
              }}
            />
            <div className="d-flex flex-wrap gap-2 mt-2">
              {assignees.map((assignee) => (
                <span key={assignee.id} className="badge text-bg-light">
                  {assignee.label}
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      aria-label="Remover"
                      onClick={() =>
                        setAssignees((prev) => prev.filter((item) => item.id !== assignee.id))
                      }
                    />
                  )}
                </span>
              ))}
              {assignees.length === 0 && (
                <span className="text-muted small">Nenhum responsavel.</span>
              )}
            </div>
          </div>
        </form>
      </CrudModal>
    </>
  );
};
